// Settings › Visibility (spec sale-filter-self-service.md): the retailer's own sale filters. Balance
// Control joins this tab later. Every change goes through a confirmation that says what it does.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import {
  BASE_HINT,
  BASE_LABEL,
  DEEPER_LABEL,
  OFF_SUMMARY,
  changeDialog,
  changedLine,
  deeperHint,
  effectRows,
  thresholdLabel,
  thresholdOption,
} from './saleFilterCopy';

export default function VisibilityTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const retailerParam = searchParams.get('retailer') || undefined;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [pending, setPending] = useState(null); // the state a confirmed change would save
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    return dashboardAPI
      .getSaleFilterSettings(retailerParam)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [retailerParam]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await dashboardAPI.saveSaleFilterSettings(pending, retailerParam);
      track('Sale Filter Changed', { base: pending.base, deeper: pending.deeper, threshold_pp: pending.threshold_pp });
      setNotice('Saved. The change works within a minute.');
      setPending(null);
      await load();
    } catch (e) {
      setPending(null);
      setError(e.message);
    }
    setBusy(false);
  };

  if (!data && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const current = data && { base: data.base, deeper: data.deeper, threshold_pp: data.threshold_pp };
  const editable = data?.can_edit && !busy;
  const dialog = pending && changeDialog(current, pending, data?.changed?.recent);
  // A filter just turned on has only zeros: say there are no numbers yet instead.
  const rows = effectRows(data?.effect);
  const hasNumbers = rows.some(([, value]) => value !== '0');
  // An off filter whose last change was ours (often the audit's first fill) has nothing to say.
  const footer = data && (current.base || data.changed?.by !== 'HandOff') ? changedLine(data.changed) : null;

  return (
    <Stack spacing={2.5}>
      {data?.group?.length > 1 && (
        <TextField
          select
          label="Shop"
          value={data.retailer_id}
          onChange={(e) => setSearchParams(e.target.value ? { retailer: e.target.value } : {})}
          sx={{ maxWidth: 360 }}
        >
          {data.group.map((g) => (
            <MenuItem key={g.retailer_id} value={g.retailer_id}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {data && !data.can_edit && (
        <Alert severity="info">
          Only admin users can change these settings. Ask an admin in your team, or{' '}
          <Link href="mailto:info@handoffleads.com">contact HandOff</Link>.
        </Alert>
      )}

      {data && (
        <Card>
          <CardContent sx={{ px: { xs: 2, sm: 3 }, '&:last-child': { pb: 2 } }}>
            <Typography variant="h6" component="h2" sx={{ fontSize: 18, mb: 1 }}>
              Sale items
            </Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ py: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{BASE_LABEL}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {BASE_HINT}
                </Typography>
              </Box>
              <FormControlLabel
                labelPlacement="start"
                label={current.base ? 'On' : 'Off'}
                sx={{ mr: 0, flex: 'none' }}
                control={
                  <Switch
                    checked={current.base}
                    disabled={!editable}
                    onChange={() => setPending({ ...current, base: !current.base, deeper: current.base ? false : current.deeper })}
                    inputProps={{ 'aria-label': BASE_LABEL }}
                  />
                }
              />
            </Stack>

            {current.base && (
              <Box sx={{ py: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{DEEPER_LABEL}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {deeperHint(current.threshold_pp)}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    labelPlacement="start"
                    label={current.deeper ? 'On' : 'Off'}
                    sx={{ mr: 0, flex: 'none' }}
                    control={
                      <Switch
                        checked={current.deeper}
                        disabled={!editable}
                        onChange={() => setPending({ ...current, deeper: !current.deeper })}
                        inputProps={{ 'aria-label': DEEPER_LABEL }}
                      />
                    }
                  />
                </Stack>
                {current.deeper && (
                <TextField
                  select
                  label={thresholdLabel}
                  value={current.threshold_pp}
                  disabled={!editable}
                  onChange={(e) => setPending({ ...current, threshold_pp: Number(e.target.value) })}
                  sx={{ mt: 1.5, minWidth: 220 }}
                >
                  {data.threshold_options.map((t) => (
                    <MenuItem key={t} value={t}>
                      {thresholdOption(t)}
                    </MenuItem>
                  ))}
                </TextField>
                )}
              </Box>
            )}

            <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              {!current.base ? (
                <Typography color="text.secondary">{OFF_SUMMARY}</Typography>
              ) : hasNumbers ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Last 30 days
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, rowGap: 1.5 }}>
                    {rows.map(([label, value]) => (
                      <Box key={label}>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Typography color="text.secondary">No numbers yet. They show here from tomorrow.</Typography>
              )}
            </Box>

            {footer && (
              <Typography variant="caption" component="div" color="text.secondary" sx={{ pt: 1.5 }}>
                {footer}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!dialog} onClose={() => setPending(null)} fullScreen={fullScreen} maxWidth="sm">
        {dialog && (
          <>
            <DialogTitle>{dialog.title}</DialogTitle>
            <DialogContent>
              {dialog.lines.map((line) => (
                <Typography key={line} gutterBottom>
                  {line}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPending(null)}>Cancel</Button>
              <Button variant="contained" onClick={save} disabled={busy}>
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}
